<template>
  <div class="dashboard-ui">
    <div v-if="isSidebarAllowed" class="dashboard-ui__sidebar">
      <div class="dashboard-ui__menu-btn">
        <MenuBtn ref="menuBtn" @click="showSidebar" />
      </div>
      <Transition name="sidebar" @after-leave="navigate">
        <div
          v-show="isSidebarShown"
          ref="sidebar"
          v-click-outside="{
            active: isSidebarShown,
            handler: handleSidebarClickOutside,
          }"
          class="dashboard-ui__sidebar-content"
        >
          <slot
            name="sidebar"
            v-bind="{
              vOnNavigate: onNavigate,
              vOnClose: hideSidebar,
            }"
            :is-sidebar-shown="isSidebarShown"
          />
        </div>
      </Transition>
    </div>
    <div class="dashboard-ui__content">
      <div v-if="backTo && breadcrumbs.length" class="dashboard-ui__pre-header">
        <BackBtn :to="backTo" />
      </div>
      <DashboardHeader :breadcrumbs="breadcrumbs">
        <template v-if="backTo" #menu>
          <BackBtn :to="backTo" />
        </template>
        <template #logout-btn>
          <slot name="logout-btn" />
        </template>
      </DashboardHeader>
      <main class="dashboard-ui__main">
        <slot />
      </main>
    </div>
  </div>
</template>

<script lang="ts" setup>
  // Types
  import type { PropType } from 'vue';
  import type { RouteLocationNormalizedLoaded } from 'vue-router';
  import type { TUser } from '~/misc/auth/types';

  // Components
  import DashboardHeader from '~/modules/dashboard/header/DashboardHeader.vue';
  import MenuBtn from '~/misc/btn/menu/MenuBtn.vue';
  import BackBtn from '~/misc/btn/back/BackBtn.vue';

  defineProps({
    user: {
      type: Object as PropType<TUser | null>,
      default: null,
    },
    breadcrumbs: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    isSidebarAllowed: {
      type: Boolean,
      default: true,
    },
    backTo: {
      type: String,
      required: false,
    },
  });

  const isSidebarShown: Ref<boolean> = ref<boolean>(false);

  const navigateToOnSidebarClose: Ref<RouteLocationNormalizedLoaded | string | null> = ref<
    RouteLocationNormalizedLoaded | string | null
  >(null);

  const sidebar: Ref<HTMLDivElement | null> = ref<HTMLDivElement | null>(null);

  const menuBtn: Ref<InstanceType<typeof MenuBtn> | null> = ref<InstanceType<
    typeof MenuBtn
  > | null>(null);

  const setIsSidebarShown = (value: boolean) => {
    isSidebarShown.value = value;
  };

  const showSidebar = () => setIsSidebarShown(true);

  const hideSidebar = () => setIsSidebarShown(false);

  const handleSidebarClickOutside = (event: MouseEvent | KeyboardEvent) => {
    const sidebarEl: HTMLDivElement | null = sidebar.value;

    if (!sidebarEl) {
      return;
    }

    const target: EventTarget | null = event.target;

    if (sidebarEl.contains(target as Node)) {
      return;
    }

    hideSidebar();
  };

  const onNavigate = (route: RouteLocationNormalizedLoaded | string) => {
    navigateToOnSidebarClose.value = route;

    hideSidebar();
  };

  const navigate = () => {
    if (!navigateToOnSidebarClose.value) return;

    navigateTo(navigateToOnSidebarClose.value);

    navigateToOnSidebarClose.value = null;
  };

  watch(isSidebarShown, async (value: boolean) => {
    if (!value) {
      await nextTick();

      menuBtn.value?.$el.focus();
    }
  });
</script>
